import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connector, leaderboard as leaderboardApi } from '../../services/api';
import type {
  EvaluatorKind,
  LeaderboardProvider,
  MetaDataset,
  MetaDatasetEntry,
  MetricBlock,
  MetricDirection,
  SplitEntry,
} from '../../types/leaderboard';
import {
  EVALUATORS,
  METRIC_BLOCKS,
  buildMetaDatasetPayload,
  evaluatorDefinition,
  formatMetricPath,
  metaDatasetNormalizationWarnings,
  validateMetaDataset,
  type MetaDatasetDraft,
  type ValidationIssue,
} from '../../utils/leaderboardSpec';

/**
 * Создание мета-датасета (AIT-117, FE-1). Лидерборд заводится вместе с ним.
 *
 * Датасеты — плоский список (BE-5): ML сам режет каждый по его собственным
 * сплитам, поэтому здесь больше нет трёх разделов train/validation/test и
 * split policy (seed, доли) — этому нечего описывать. Оценщик один на весь
 * мета-датасет, а не на пару (датасет, сплит).
 *
 * Датасеты подтягиваются списком: интеграция выбирается один раз наверху, дальше
 * виден человеческий заголовок, а не UUID. Ручной ввод id оставлен как запасной
 * путь — датасет может лежать вне выбранной интеграции.
 */

interface Props {
  onClose: () => void;
  /** Отдаёт сохранённую запись — вызывающая сторона решает, форкнула ли правка
   *  мета-датасет (`forked: true`, другие `id`/`slug`) и на какое табло
   *  переключиться. */
  onCreated: (saved?: MetaDataset) => void;
  initialData?: MetaDataset;
}

interface DatasetOption {
  id: string;
  label: string;
}

/** Датасет вместе с интеграцией, из каталога которой он пришёл. */
interface DatasetChoice extends DatasetOption {
  integrationId: string;
  integrationLabel: string;
}

function groupDatasetsByIntegration(
  datasets: DatasetChoice[]
): Array<{ integrationId: string; integrationLabel: string; items: DatasetChoice[] }> {
  const groups = new Map<string, { integrationId: string; integrationLabel: string; items: DatasetChoice[] }>();
  datasets.forEach((d) => {
    const group = groups.get(d.integrationId);
    if (group) group.items.push(d);
    else groups.set(d.integrationId, { integrationId: d.integrationId, integrationLabel: d.integrationLabel, items: [d] });
  });
  return Array.from(groups.values());
}

const responseError = (response: unknown, fallback: string): string => {
  const value = response as { error?: string | { message?: string }; message?: string } | null | undefined;
  if (typeof value?.error === 'string') return value.error;
  return value?.error?.message || value?.message || fallback;
};

const requestError = (error: unknown, fallback: string): string => {
  const value = error as { message?: string; response?: { status?: number; data?: unknown } };
  if (value.response?.status === 409) return 'A meta-dataset with this slug already exists.';
  return responseError(value.response?.data, value.message || fallback);
};

const METRIC_OPTIONS = [
  { value: 'score', label: 'Score' },
  { value: 'f1', label: 'F1' },
  { value: 'precision', label: 'Precision' },
  { value: 'recall', label: 'Recall' },
] as const;

/**
 * Мета-датасет, отданный на чтение, всё ещё раскладывает пары по трём
 * сплитам (BE ещё не переехал на плоский ответ). Для формы редактирования
 * достаточно одного взгляда: датасет, добавленный флоской формой, лежит в
 * train/validation/test одинаково — эвалюатор и id совпадают везде.
 */
function deriveInitialState(data: MetaDataset | undefined): {
  entries: MetaDatasetEntry[];
  evaluatorKind: EvaluatorKind;
  evaluatorConfig: Record<string, unknown>;
} {
  if (!data) return { entries: [], evaluatorKind: 'bert_score', evaluatorConfig: {} };
  const splits = [data.train, data.validation, data.test].filter(
    (s): s is SplitEntry[] => Array.isArray(s) && s.length > 0
  );
  const seen = new Set<string>();
  const entries: MetaDatasetEntry[] = [];
  splits.flat().forEach((item) => {
    if (item.dataset_id && !seen.has(item.dataset_id)) {
      seen.add(item.dataset_id);
      entries.push({ dataset_id: item.dataset_id });
    }
  });
  const first = splits[0]?.[0];
  return {
    entries,
    evaluatorKind: (first?.evaluator_kind as EvaluatorKind) ?? 'bert_score',
    evaluatorConfig: first?.evaluator_config ?? {},
  };
}

const DatasetListEditor: React.FC<{
  entries: MetaDatasetEntry[];
  datasets: DatasetChoice[];
  datasetsLoading: boolean;
  manual: Record<string, boolean>;
  onToggleManual: (key: string, value: boolean) => void;
  onChange: (next: MetaDatasetEntry[]) => void;
}> = ({ entries, datasets, datasetsLoading, manual, onToggleManual, onChange }) => {
  const patch = (i: number, next: Partial<MetaDatasetEntry>) =>
    onChange(entries.map((e, idx) => (idx === i ? { ...e, ...next } : e)));

  return (
    <section className="lb-split">
      <header className="lb-split__head">
        <h4>Datasets</h4>
        <span className="lb-muted">ML splits each dataset into train, validation and test itself.</span>
        <button type="button" className="lb-btn lb-btn--xs" onClick={() => onChange([...entries, { dataset_id: '' }])}>
          + Dataset
        </button>
      </header>

      {entries.length === 0 ? (
        <p className="lb-muted lb-split__empty">No datasets yet.</p>
      ) : (
        <div className="lb-split__rows">
          {entries.map((entry, i) => {
            const key = `ds:${i}`;
            // Ручной режим сам включается для id, которого нет в списке — иначе
            // при смене интеграции выбранный датасет молча стал бы пустым.
            const known = datasets.some((d) => d.id === entry.dataset_id);
            const isManual = manual[key] ?? (!!entry.dataset_id && !known);

            return (
              <div key={i} className="lb-split__row">
                <div className="lb-field lb-field--grow">
                  <span className="lb-field__label">
                    Dataset
                    <button
                      type="button"
                      className="lb-linkbtn"
                      onClick={() => onToggleManual(key, !isManual)}
                    >
                      {isManual ? 'pick from list' : 'enter id'}
                    </button>
                  </span>
                  {isManual ? (
                    <input
                      className="lb-input"
                      value={entry.dataset_id}
                      placeholder="dataset uuid"
                      onChange={(e) => patch(i, { dataset_id: e.target.value.trim() })}
                    />
                  ) : (
                    <select
                      className="lb-input"
                      value={entry.dataset_id}
                      disabled={datasetsLoading || datasets.length === 0}
                      onChange={(e) => patch(i, { dataset_id: e.target.value })}
                    >
                      <option value="">
                        {datasetsLoading ? 'Loading…' : datasets.length ? 'Pick a dataset…' : 'No datasets available'}
                      </option>
                      {/* Всё сразу, сгруппированное по интеграции: выбирать датасет
                          после отдельного выбора интеграции — лишний шаг. */}
                      {groupDatasetsByIntegration(datasets).map((group) => (
                        <optgroup key={group.integrationId} label={group.integrationLabel}>
                          {group.items.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  )}
                </div>

                <button
                  type="button"
                  className="lb-btn lb-btn--xs lb-btn--danger"
                  title="Remove dataset"
                  onClick={() => onChange(entries.filter((_, idx) => idx !== i))}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

const MetaDatasetForm: React.FC<Props> = ({ onClose, onCreated, initialData }) => {
  const initialMetric = initialData?.leaderboard?.primary_metric_path?.split('.') ?? [];
  const initialDerived = deriveInitialState(initialData);
  const [slug, setSlug] = useState(initialData?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(false);
  const [name, setName] = useState(initialData?.name ?? '');
  const initialModelConfig = initialData?.leaderboard?.model_config?.config;
  const [modelPrompt, setModelPrompt] = useState(String(initialModelConfig?.prompt ?? ''));
  const [modelName, setModelName] = useState(String(initialModelConfig?.model ?? 'gpt-4o-mini'));
  const [modelTemperature, setModelTemperature] = useState<number | undefined>(
    typeof initialModelConfig?.temperature === 'number' ? initialModelConfig.temperature : 0
  );
  const [maxParallelism, setMaxParallelism] = useState<number | undefined>(
    initialData?.leaderboard?.max_parallelism
  );
  const [parallelCalls, setParallelCalls] = useState<number | undefined>(
    initialData?.leaderboard?.parallel_calls
  );
  const [datasetEntries, setDatasetEntries] = useState<MetaDatasetEntry[]>(
    initialDerived.entries.length ? initialDerived.entries : [{ dataset_id: '' }]
  );
  const [evaluatorKind, setEvaluatorKind] = useState<EvaluatorKind>(initialDerived.evaluatorKind);
  const [evaluatorConfig, setEvaluatorConfig] = useState<Record<string, unknown>>(initialDerived.evaluatorConfig);
  const [metricBlock, setMetricBlock] = useState<MetricBlock>((initialMetric[0] as MetricBlock) || 'validation');
  const [metricName, setMetricName] = useState(initialMetric.slice(1).join('.') || 'score');
  const [metricDirection, setMetricDirection] = useState<MetricDirection>(initialData?.leaderboard?.metric_direction ?? 'maximize');
  const [timeoutS, setTimeoutS] = useState<number | undefined>(initialData?.leaderboard?.budget?.timeout_s ?? 300);
  const [maxIterations, setMaxIterations] = useState<number | undefined>(initialData?.leaderboard?.budget?.max_iterations ?? 1);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showIssues, setShowIssues] = useState(false);
  const [manual, setManual] = useState<Record<string, boolean>>({});

  const [datasetChoices, setDatasetChoices] = useState<DatasetChoice[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(true);

  const [providers, setProviders] = useState<LeaderboardProvider[]>([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [providersError, setProvidersError] = useState<string | null>(null);
  // `undefined` — режим решается автоматически по каталогу; явный клик переопределяет.
  const [modelManualOverride, setModelManualOverride] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    setDatasetsLoading(true);
    void (async () => {
      try {
        const resp = (await connector.listIntegrationsAdmin()) as
          | { data?: unknown; items?: unknown }
          | unknown[];
        const raw = Array.isArray(resp)
          ? resp
          : (resp as { data?: unknown; items?: unknown })?.data ?? (resp as { items?: unknown })?.items ?? [];
        const integrations = ((Array.isArray(raw) ? raw : []) as Array<Record<string, unknown>>)
          .map((it) => ({ id: String(it.id ?? ''), label: String(it.url ?? it.name ?? it.id ?? '') }))
          .filter((o) => o.id);

        // Каталог датасетов поклиентский, общей ручки нет — собираем все каталоги
        // сразу, чтобы выбирать датасет одним движением, а не после интеграции.
        const perIntegration = await Promise.all(
          integrations.map(async (integration) => {
            try {
              const res = await connector.getDatasets(integration.id);
              const list = Array.isArray(res.data) ? res.data : [];
              return list
                .map((d) => ({
                  id: String(d.id ?? ''),
                  label: String(d.label ?? d.id ?? ''),
                  integrationId: integration.id,
                  integrationLabel: integration.label,
                }))
                .filter((d) => d.id);
            } catch {
              // Недоступная интеграция не должна прятать датасеты остальных.
              return [] as DatasetChoice[];
            }
          })
        );
        if (!alive) return;
        setDatasetChoices(perIntegration.flat());
      } catch {
        // Каталог — удобство, а не условие работы: остаётся ручной ввод id.
        if (alive) setDatasetChoices([]);
      } finally {
        if (alive) setDatasetsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setProvidersLoading(true);
    setProvidersError(null);
    void (async () => {
      try {
        const res = await leaderboardApi.listProviders();
        if (!alive) return;
        if (!res.success) {
          setProviders([]);
          setProvidersError(responseError(res, 'Could not load the model catalogue.'));
          return;
        }
        setProviders(res.data?.providers ?? []);
      } catch (e) {
        // Каталог — удобство, а не условие работы: остаётся ручной ввод имени модели.
        if (alive) {
          setProviders([]);
          setProvidersError(requestError(e, 'Could not load the model catalogue.'));
        }
      } finally {
        if (alive) setProvidersLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const chosenDatasetIds = datasetEntries.map((entry) => entry.dataset_id).filter(Boolean);

  /**
   * Мета-датасет записывается под одну интеграцию — по ней бэкенд разрешает
   * идентификаторы в имена объектов в S3. Поэтому её не выбирают, а выводят из
   * выбранных датасетов; смешать каталоги двух интеграций нельзя.
   */
  const chosenIntegrations = Array.from(
    new Set(
      chosenDatasetIds
        .map((id) => datasetChoices.find((d) => d.id === id)?.integrationId)
        .filter((id): id is string => !!id)
    )
  );
  const integrationId =
    chosenIntegrations.length === 1 ? chosenIntegrations[0] : initialData?.integration_id ?? '';
  const integrationConflict =
    chosenIntegrations.length > 1
      ? `Datasets come from ${chosenIntegrations.length} different integrations — a meta-dataset is stored under one, so the backend could not resolve them all.`
      : null;

  const draft: MetaDatasetDraft = {
    integrationId,
    maxParallelism,
    parallelCalls,
    slug,
    name,
    modelPrompt,
    modelName,
    modelTemperature,
    datasets: datasetEntries,
    evaluatorKind,
    evaluatorConfig,
    metricPath: formatMetricPath(metricBlock, metricName),
    metricDirection,
    timeoutS,
    maxIterations,
    // Форма его не показывает — переносится как есть, иначе правка через новую
    // форму молча обнулила бы seed и доли старой записи.
    splitPolicy: initialData?.split_policy ?? undefined,
  };

  // Считаем на каждый рендер: пара проходов по нескольким строкам стоит дешевле,
  // чем список зависимостей, который однажды разойдётся с содержимым черновика.
  const issues: ValidationIssue[] = validateMetaDataset(draft);
  // Один раз на запись: показывает, что теряется при переводе старой формы
  // разделов в плоский список с одним оценщиком — до сохранения, не после.
  const normalizationWarnings = useMemo(() => metaDatasetNormalizationWarnings(initialData), [initialData]);

  const setManualFor = useCallback((key: string, value: boolean) => {
    setManual((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Пока slug не трогали руками — выводим его из названия: набирать дважды незачем.
  const onNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) {
      setSlug(
        value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_+|_+$/g, '')
      );
    }
  };

  const submit = async () => {
    setShowIssues(true);
    setServerError(null);
    if (issues.length > 0 || integrationConflict) return;
    setSubmitting(true);
    try {
      const payload = buildMetaDatasetPayload(draft);
      const response = initialData
        ? await leaderboardApi.updateMetaDataset(initialData.id, payload)
        : await leaderboardApi.createMetaDataset(payload);
      if (!response.success) {
        setServerError(responseError(response, `Could not ${initialData ? 'update' : 'create'} the meta-dataset.`));
        return;
      }
      onCreated(response.data);
      onClose();
    } catch (e) {
      // Текст бэкенда человекочитаемый — показываем как есть, он точнее нашего.
      setServerError(requestError(e, `Could not ${initialData ? 'update' : 'create'} the meta-dataset.`));
    } finally {
      setSubmitting(false);
    }
  };

  const evaluatorDef = evaluatorDefinition(evaluatorKind);
  const modelKnown = providers.some((p) => p.models.includes(modelName));
  const modelManual = modelManualOverride ?? (providersLoading ? false : !modelKnown);
  const unavailableProviders = providers.filter((p) => p.error);

  return (
    <div className="lb-modal-overlay" role="dialog" aria-modal="true" aria-label={initialData ? 'Edit meta-dataset' : 'New meta-dataset'} onClick={onClose}>
      <div className="lb-modal lb-modal--wide" onClick={(e) => e.stopPropagation()}>
        <header className="lb-modal__head">
          <div className="lb-modal__title">
            <h3>{initialData ? 'Edit meta-dataset' : 'New meta-dataset'}</h3>
            <span className="lb-muted">{initialData ? 'The complete definition will be replaced.' : 'A leaderboard is created together with it.'}</span>
          </div>
          <button type="button" className="lb-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="lb-modal__body" data-testid="lb-meta-form">
          <div className="lb-row">
            <label className="lb-field lb-field--grow">
              <span className="lb-field__label">Name</span>
              <input className="lb-input" value={name} onChange={(e) => onNameChange(e.target.value)} placeholder="Cactus v1" />
            </label>
            <label className="lb-field lb-field--grow">
              <span className="lb-field__label">Slug</span>
              <input
                className="lb-input"
                value={slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setSlug(e.target.value);
                }}
                placeholder="cactus_v1"
              />
              <span className="lb-field__hint">Filled in from the name; edit if you need another one.</span>
            </label>
          </div>

          <DatasetListEditor
            entries={datasetEntries}
            datasets={datasetChoices}
            datasetsLoading={datasetsLoading}
            manual={manual}
            onToggleManual={setManualFor}
            onChange={setDatasetEntries}
          />

          <div className="lb-row">
            <label className="lb-field">
              <span className="lb-field__label">Evaluator</span>
              <select
                className="lb-input"
                value={evaluatorKind}
                onChange={(e) => {
                  setEvaluatorKind(e.target.value as EvaluatorKind);
                  setEvaluatorConfig({});
                }}
              >
                {EVALUATORS.map((ev) => (
                  <option key={ev.kind} value={ev.kind}>
                    {ev.label}
                  </option>
                ))}
              </select>
              <span className="lb-field__hint">Scores every dataset in this meta-dataset.</span>
            </label>
            {evaluatorDef?.requires ? (
              <label className="lb-field lb-field--grow">
                <span className="lb-field__label">{evaluatorDef.requires}</span>
                <input
                  className="lb-input"
                  value={String(evaluatorConfig?.[evaluatorDef.requires] ?? '')}
                  placeholder={evaluatorDef.requires === 'prompt' ? 'Score the answer from 0 to 100' : 'path/to/artifact'}
                  // Слить с текущим конфигом, а не заменить целиком: у записи,
                  // заведённой до этой формы, llm-judge мог нести ещё и свою
                  // модель или temperature рядом с prompt — их нельзя терять
                  // при правке одного поля.
                  onChange={(e) => setEvaluatorConfig((prev) => ({ ...prev, [evaluatorDef.requires as string]: e.target.value }))}
                />
              </label>
            ) : null}
          </div>

          {integrationConflict ? (
            <div className="lb-banner lb-banner--danger" data-testid="lb-integration-conflict">
              <p>{integrationConflict}</p>
            </div>
          ) : null}

          {normalizationWarnings.length > 0 ? (
            <div className="lb-banner lb-banner--warn" data-testid="lb-normalization-warning">
              <p>Saving normalizes this meta-dataset to the new form (one dataset list, one evaluator):</p>
              {normalizationWarnings.map((w) => (
                <p key={w}>{w}</p>
              ))}
            </div>
          ) : null}

          <details className="lb-more">
            <summary>Model and budget</summary>
            <div className="lb-more__body">
              <div className="lb-model-config">
                <div className="lb-model-config__head">
                  <div>
                    <strong>Model config</strong>
                    <span>Provider: OpenAI</span>
                  </div>
                  <code>kind: openai</code>
                </div>
                <label className="lb-field">
                  <span className="lb-field__label">System prompt</span>
                  <textarea
                    className="lb-input lb-textarea"
                    rows={3}
                    value={modelPrompt}
                    onChange={(e) => setModelPrompt(e.target.value)}
                    placeholder="Describe how the model should answer benchmark requests"
                  />
                </label>
                <div className="lb-row">
                  <label className="lb-field lb-field--grow">
                    <span className="lb-field__label">
                      OpenAI model
                      <button type="button" className="lb-linkbtn" onClick={() => setModelManualOverride(!modelManual)}>
                        {modelManual ? 'pick from catalogue' : 'enter manually'}
                      </button>
                    </span>
                    {modelManual ? (
                      <input
                        className="lb-input"
                        value={modelName}
                        onChange={(e) => setModelName(e.target.value)}
                        placeholder="gpt-4o-mini"
                      />
                    ) : (
                      <select
                        className="lb-input"
                        value={modelName}
                        disabled={providersLoading || providers.every((p) => p.models.length === 0)}
                        onChange={(e) => setModelName(e.target.value)}
                      >
                        <option value="">
                          {providersLoading ? 'Loading…' : 'Pick a model…'}
                        </option>
                        {providers.map((p) => (
                          p.models.length > 0 ? (
                            <optgroup key={p.provider} label={p.title || p.provider}>
                              {p.models.map((m) => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </optgroup>
                          ) : null
                        ))}
                      </select>
                    )}
                    {providersError ? (
                      <span className="lb-field__hint lb-field__hint--warn">{providersError}</span>
                    ) : unavailableProviders.length > 0 ? (
                      <span className="lb-field__hint">
                        {unavailableProviders.map((p) => `${p.title || p.provider}: ${p.error}`).join(' · ')}
                      </span>
                    ) : null}
                  </label>
                  <label className="lb-field">
                    <span className="lb-field__label">Parallel algorithms</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="lb-input"
                      value={maxParallelism ?? ''}
                      onChange={(e) => setMaxParallelism(e.target.value === '' ? undefined : Number(e.target.value))}
                      placeholder="1"
                    />
                  </label>
                  <label className="lb-field">
                    <span className="lb-field__label">Parallel calls</span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      className="lb-input"
                      value={parallelCalls ?? ''}
                      onChange={(e) => setParallelCalls(e.target.value === '' ? undefined : Number(e.target.value))}
                      placeholder="1"
                    />
                  </label>
                  <label className="lb-field">
                    <span className="lb-field__label">Temperature</span>
                    <input
                      type="number"
                      step="0.1"
                      className="lb-input"
                      value={modelTemperature ?? ''}
                      onChange={(e) => setModelTemperature(e.target.value === '' ? undefined : Number(e.target.value))}
                    />
                  </label>
                </div>
              </div>

              <div className="lb-row">
                <label className="lb-field">
                  <span className="lb-field__label">Timeout, s</span>
                  <input
                    type="number"
                    className="lb-input"
                    value={timeoutS ?? ''}
                    onChange={(e) => setTimeoutS(e.target.value === '' ? undefined : Number(e.target.value))}
                  />
                </label>
                <label className="lb-field">
                  <span className="lb-field__label">Max iterations</span>
                  <input
                    type="number"
                    className="lb-input"
                    value={maxIterations ?? ''}
                    onChange={(e) => setMaxIterations(e.target.value === '' ? undefined : Number(e.target.value))}
                  />
                </label>
              </div>
            </div>
          </details>

          <div className="lb-row">
            <label className="lb-field">
              <span className="lb-field__label">Rank by</span>
              <select className="lb-input" value={metricBlock} onChange={(e) => setMetricBlock(e.target.value as MetricBlock)}>
                {METRIC_BLOCKS.map((b) => (
                  <option key={b.block} value={b.block}>
                    {b.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="lb-field lb-field--grow">
              <span className="lb-field__label">Metric</span>
              <select className="lb-input" value={metricName} onChange={(e) => setMetricName(e.target.value)}>
                {!METRIC_OPTIONS.some((metric) => metric.value === metricName) && metricName ? (
                  <option value={metricName}>{metricName} (current)</option>
                ) : null}
                {METRIC_OPTIONS.map((metric) => (
                  <option key={metric.value} value={metric.value}>{metric.label}</option>
                ))}
              </select>
              <span className="lb-field__hint">
                Ranking path: <code>{draft.metricPath}</code>
              </span>
            </label>
            <label className="lb-field">
              <span className="lb-field__label">Direction</span>
              <select
                className="lb-input"
                value={metricDirection}
                onChange={(e) => setMetricDirection(e.target.value as MetricDirection)}
              >
                <option value="maximize">Higher is better</option>
                <option value="minimize">Lower is better</option>
              </select>
            </label>
          </div>

          {showIssues && issues.length > 0 ? (
            <div className="lb-banner lb-banner--danger" data-testid="lb-issues">
              {issues.map((i) => (
                <p key={i.field + i.message}>{i.message}</p>
              ))}
            </div>
          ) : null}

          {serverError ? <div className="lb-banner lb-banner--danger">{serverError}</div> : null}
        </div>

        <footer className="lb-modal__foot">
          {issues.length === 0 && !integrationConflict ? (
            <span className="lb-muted">Ready to create</span>
          ) : (
            <button type="button" className="lb-linkbtn" onClick={() => setShowIssues((v) => !v)}>
              {issues.length} {issues.length === 1 ? 'thing' : 'things'} to fix
            </button>
          )}
          <div className="lb-modal__actions">
            <button type="button" className="lb-btn" onClick={onClose}>
              Cancel
            </button>
            {/* Конфликт интеграций виден баннером сразу, поэтому кнопку гасим:
                нажатие всё равно ничего бы не сделало. Остальные замечания
                раскрываются по нажатию, там кнопка активна. */}
            <button
              type="button"
              className="lb-btn lb-btn--primary"
              onClick={() => void submit()}
              disabled={submitting || !!integrationConflict}
              title={integrationConflict ?? undefined}
            >
              {submitting ? (initialData ? 'Saving…' : 'Creating…') : (initialData ? 'Save changes' : 'Create')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MetaDatasetForm;
